/**
 * Panel components for session history, visit history, and filter configuration.
 */

import { i18n, getLanguage } from './i18n';
import { escapeHtml, ApiService, LocationService, SessionSummary, SessionsResponse, SessionHistoryResponse, ToolResultSummary, getPreferredCountryCode, normalizeCountryCode, getPlacesLanguageCode, getStoredLocation } from './services';
import { Restaurant, TravelMode, STORAGE_KEYS } from './types';

// ============================================================
// SESSION HISTORY PANEL
// ============================================================

export interface SessionLoadedEvent {
    sessionId: string;
    model: string;
    messages: Array<{ role: string; content: string; thoughts?: string; tool_results?: ToolResultSummary[] | null }>;
    createdAt: string;
}

export class SessionHistoryPanel {
    private panel: HTMLElement;
    private overlay: HTMLElement;
    private closeBtn: HTMLButtonElement;
    private list: HTMLElement;
    private api: ApiService;
    private onSessionLoaded: (event: SessionLoadedEvent) => void;

    constructor(
        panel: HTMLElement,
        overlay: HTMLElement,
        closeBtn: HTMLButtonElement,
        list: HTMLElement,
        api: ApiService,
        onSessionLoaded: (event: SessionLoadedEvent) => void
    ) {
        this.panel = panel;
        this.overlay = overlay;
        this.closeBtn = closeBtn;
        this.list = list;
        this.api = api;
        this.onSessionLoaded = onSessionLoaded;

        this.closeBtn.addEventListener('click', () => this.close());
        this.overlay.addEventListener('click', () => this.close());
    }

    async open(): Promise<void> {
        this.panel.classList.add('active');
        this.overlay.classList.add('active');
        await this.loadSessions();
    }

    close(): void {
        this.panel.classList.remove('active');
        this.overlay.classList.remove('active');
    }

    private async loadSessions(): Promise<void> {
        try {
            const data = await this.api.fetch<SessionsResponse>('api/sessions');

            if (data.sessions.length === 0) {
                this.list.innerHTML = `<p class="history-empty">${i18n.historyEmpty}</p>`;
                return;
            }

            this.list.innerHTML = '';
            for (const session of data.sessions) {
                const item = this.createHistoryItem(session);
                this.list.appendChild(item);
            }
        } catch (error) {
            console.error('Error loading sessions:', error);
            this.list.innerHTML = `<p class="history-empty">${i18n.historyEmpty}</p>`;
        }
    }

    private createHistoryItem(session: SessionSummary): HTMLElement {
        const item = document.createElement('div');
        item.className = 'history-item';

        const date = new Date(session.created_at);
        const dateStr = date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        item.innerHTML = `
            <div class="history-item-date">${dateStr}</div>
            <div class="history-item-model">${session.initial_model}</div>
            <div class="history-item-count">${session.interaction_count} message${session.interaction_count !== 1 ? 's' : ''}</div>
        `;

        item.addEventListener('click', () => this.loadSession(session.filename));

        return item;
    }

    private async loadSession(filename: string): Promise<void> {
        try {
            const data = await this.api.fetch<SessionHistoryResponse>(`api/sessions/${filename}`);

            this.onSessionLoaded({
                sessionId: data.session_id,
                model: data.initial_model,
                messages: data.messages,
                createdAt: data.created_at
            });

            this.close();
        } catch (error) {
            console.error('Error loading session:', error);
        }
    }
}

// ============================================================
// VISIT HISTORY PANEL
// ============================================================

interface VisitWithRestaurant {
    restaurantId: string;
    restaurantName: string;
    timestamp: string;
    note: string;
    index: number;
}

export class VisitHistoryPanel {
    private panel: HTMLElement;
    private overlay: HTMLElement;
    private closeBtn: HTMLButtonElement;
    private list: HTMLElement;
    private sortSelect: HTMLSelectElement;
    private api: ApiService;
    private getRestaurants: () => Restaurant[];
    private onVisitDeleted: (restaurantId: string) => Promise<void>;
    private onVisitEdited: (restaurantId: string, visitIndex: number, note: string) => Promise<void>;

    constructor(
        panel: HTMLElement,
        overlay: HTMLElement,
        closeBtn: HTMLButtonElement,
        list: HTMLElement,
        sortSelect: HTMLSelectElement,
        api: ApiService,
        getRestaurants: () => Restaurant[],
        onVisitDeleted: (restaurantId: string) => Promise<void>,
        onVisitEdited: (restaurantId: string, visitIndex: number, note: string) => Promise<void>
    ) {
        this.panel = panel;
        this.overlay = overlay;
        this.closeBtn = closeBtn;
        this.list = list;
        this.sortSelect = sortSelect;
        this.api = api;
        this.getRestaurants = getRestaurants;
        this.onVisitDeleted = onVisitDeleted;
        this.onVisitEdited = onVisitEdited;

        this.closeBtn.addEventListener('click', () => this.close());
        this.overlay.addEventListener('click', () => this.close());
        this.sortSelect.addEventListener('change', () => this.render());
    }

    async open(): Promise<void> {
        this.panel.classList.add('active');
        this.overlay.classList.add('active');
        this.updateSortLabels();
        this.render();
    }

    close(): void {
        // Blur any focused select elements to close open dropdowns
        this.sortSelect.blur();
        this.panel.classList.remove('active');
        this.overlay.classList.remove('active');
    }

    private updateSortLabels(): void {
        const options = this.sortSelect.options;
        options[0].text = i18n.visitHistorySortNewest;
        options[1].text = i18n.visitHistorySortOldest;
        options[2].text = i18n.visitHistorySortRestaurant;
    }

    render(): void {
        const sortBy = this.sortSelect.value;
        const restaurants = this.getRestaurants();

        // Collect all visit records with restaurant info
        const allVisits: VisitWithRestaurant[] = [];
        const isZh = getLanguage() === 'zh';

        for (const restaurant of restaurants) {
            if (!restaurant.visit_records) continue;

            const restaurantName = isZh ? restaurant.name_zh : restaurant.name_en;
            restaurant.visit_records.forEach((record, index) => {
                allVisits.push({
                    restaurantId: restaurant.id,
                    restaurantName,
                    timestamp: record.timestamp,
                    note: record.note,
                    index
                });
            });
        }

        // Sort visits
        if (sortBy === 'date_desc') {
            allVisits.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        } else if (sortBy === 'date_asc') {
            allVisits.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        } else if (sortBy === 'restaurant') {
            allVisits.sort((a, b) => a.restaurantName.localeCompare(b.restaurantName));
        }

        if (allVisits.length === 0) {
            this.list.innerHTML = `<p class="visit-history-empty">${i18n.visitHistoryEmpty}</p>`;
            return;
        }

        this.list.innerHTML = '';
        for (const visit of allVisits) {
            const item = this.createVisitItem(visit);
            this.list.appendChild(item);
        }

        this.attachEventListeners();
    }

    private createVisitItem(visit: VisitWithRestaurant): HTMLElement {
        const item = document.createElement('div');
        item.className = 'visit-history-item';

        const date = new Date(visit.timestamp);
        const dateStr = date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        item.innerHTML = `
            <div class="visit-history-item-header">
                <span class="visit-history-item-restaurant">${visit.restaurantName}</span>
                <span class="visit-history-item-date">${dateStr}</span>
            </div>
            <div class="visit-history-item-note">${visit.note}</div>
            <div class="visit-history-item-actions">
                <button class="visit-history-edit-btn" data-restaurant-id="${visit.restaurantId}" data-index="${visit.index}" data-note="${escapeHtml(visit.note)}" data-timestamp="${visit.timestamp}" title="${i18n.edit}">✏️</button>
                <button class="visit-history-delete-btn" data-restaurant-id="${visit.restaurantId}" data-index="${visit.index}" title="${i18n.delete}">🗑️</button>
            </div>
        `;

        return item;
    }

    private attachEventListeners(): void {
        this.list.querySelectorAll('.visit-history-delete-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const target = e.currentTarget as HTMLButtonElement;
                const restaurantId = target.dataset.restaurantId!;
                const index = parseInt(target.dataset.index!, 10);
                await this.deleteVisit(restaurantId, index);
            });
        });

        this.list.querySelectorAll('.visit-history-edit-btn').forEach(btn => {
            btn.addEventListener('click', async (e) => {
                const target = e.currentTarget as HTMLButtonElement;
                const restaurantId = target.dataset.restaurantId!;
                const index = parseInt(target.dataset.index!, 10);
                const note = target.dataset.note!;
                await this.editVisit(restaurantId, index, note);
            });
        });
    }

    private async deleteVisit(restaurantId: string, visitIndex: number): Promise<void> {
        if (!confirm(i18n.visitHistoryDeleteConfirm)) return;

        try {
            await this.api.fetch<void>(`api/restaurants/${restaurantId}/visits/${visitIndex}`, { method: 'DELETE' });
            await this.onVisitDeleted(restaurantId);
            this.render();
        } catch (error) {
            console.error('Error deleting visit record:', error);
        }
    }

    private async editVisit(restaurantId: string, visitIndex: number, currentNote: string): Promise<void> {
        // Create modal for editing
        const modal = document.createElement('div');
        modal.className = 'visit-edit-modal-overlay';
        modal.innerHTML = `
            <div class="visit-edit-modal">
                <h3>${i18n.editVisitTitle}</h3>
                <label>${i18n.noteLabel}:</label>
                <textarea id="visitEditNote" rows="4">${currentNote}</textarea>
                <div class="visit-edit-modal-actions">
                    <button class="btn-secondary" id="visitEditCancel">${i18n.cancel}</button>
                    <button class="btn-primary" id="visitEditSave">${i18n.save}</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        const closeModal = () => modal.remove();

        modal.querySelector('#visitEditCancel')?.addEventListener('click', closeModal);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) closeModal();
        });

        modal.querySelector('#visitEditSave')?.addEventListener('click', async () => {
            const noteTextarea = modal.querySelector('#visitEditNote') as HTMLTextAreaElement;
            const newNote = noteTextarea.value.trim();

            if (!newNote) return;

            try {
                await this.api.fetch<void>(`api/restaurants/${restaurantId}/visits/${visitIndex}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ note: newNote })
                });
                closeModal();
                await this.onVisitEdited(restaurantId, visitIndex, newNote);
                this.render();
            } catch (error) {
                console.error('Error updating visit record:', error);
            }
        });
    }
}

// ============================================================
// FILTER CONFIG PANEL
// ============================================================

export interface FilterSettings {
    origin_lat: number;
    origin_lng: number;
    max_travel_time_minutes: number;
    travel_mode: string;
}

export interface FilteredRestaurant {
    id: string;
    name_en: string;
    name_zh: string;
    cuisine: string;
    travel?: {
        duration_seconds: number | null;
        duration_text: string;
    };
}

export class FilterConfigPanel {
    private overlay: HTMLElement;
    private panel: HTMLElement;
    private closeBtn: HTMLButtonElement;
    private useCurrentBtn: HTMLButtonElement;
    private locationStatus: HTMLElement;
    private addressInput: HTMLInputElement;
    private travelModeSelect: HTMLSelectElement;
    private maxMinutesInput: HTMLInputElement;
    private maxMinutesLabel: HTMLElement;
    private applyBtn: HTMLButtonElement;
    private restaurantList: HTMLElement;
    private hint: HTMLElement;
    private addressAutocomplete: HTMLElement;

    private api: ApiService;
    private location: LocationService;
    private sessionId: string;

    private configLat: number | null = null;
    private configLng: number | null = null;
    private filteredRestaurants: FilteredRestaurant[] = [];
    private autocompleteTimer: number | null = null;
    private autocompleteRequestId: number = 0;

    private onFilterApplied: (restaurants: FilteredRestaurant[]) => void;
    private onClose: () => void;

    constructor(
        overlay: HTMLElement,
        panel: HTMLElement,
        closeBtn: HTMLButtonElement,
        useCurrentBtn: HTMLButtonElement,
        locationStatus: HTMLElement,
        addressInput: HTMLInputElement,
        travelModeSelect: HTMLSelectElement,
        maxMinutesInput: HTMLInputElement,
        maxMinutesLabel: HTMLElement,
        applyBtn: HTMLButtonElement,
        restaurantList: HTMLElement,
        hint: HTMLElement,
        addressAutocomplete: HTMLElement,
        api: ApiService,
        location: LocationService,
        getSessionId: () => string,
        onFilterApplied: (restaurants: FilteredRestaurant[]) => void,
        onClose: () => void
    ) {
        this.overlay = overlay;
        this.panel = panel;
        this.closeBtn = closeBtn;
        this.useCurrentBtn = useCurrentBtn;
        this.locationStatus = locationStatus;
        this.addressInput = addressInput;
        this.travelModeSelect = travelModeSelect;
        this.maxMinutesInput = maxMinutesInput;
        this.maxMinutesLabel = maxMinutesLabel;
        this.applyBtn = applyBtn;
        this.restaurantList = restaurantList;
        this.hint = hint;
        this.addressAutocomplete = addressAutocomplete;
        this.api = api;
        this.location = location;
        this.sessionId = getSessionId();
        this.onFilterApplied = onFilterApplied;
        this.onClose = onClose;

        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        this.overlay.addEventListener('click', () => this.close());
        this.closeBtn.addEventListener('click', () => this.close());
        this.useCurrentBtn.addEventListener('click', () => this.populateFromCurrentLocation());
        this.applyBtn.addEventListener('click', () => this.apply());

        this.addressInput.addEventListener('input', () => {
            if (this.addressInput.value.trim().length > 0) {
                this.configLat = null;
                this.configLng = null;
                this.updateLocationStatus();
            }
            this.scheduleAutocomplete();
        });

        this.addressInput.addEventListener('blur', () => {
            setTimeout(() => this.addressAutocomplete.classList.add('hidden'), 200);
        });

        this.addressInput.addEventListener('focus', () => {
            if (this.addressInput.value.trim().length >= 2) {
                this.scheduleAutocomplete();
            }
        });

        this.maxMinutesInput.addEventListener('input', () => {
            this.maxMinutesLabel.textContent = this.maxMinutesInput.value;
        });
    }

    updateSessionId(sessionId: string): void {
        this.sessionId = sessionId;
    }

    open(hintText?: string): void {
        // Show or hide hint message
        if (hintText) {
            this.hint.textContent = hintText;
            this.hint.classList.remove('hidden');
        } else {
            this.hint.classList.add('hidden');
        }

        // Load saved settings
        const savedMode = localStorage.getItem(STORAGE_KEYS.RECOMMEND_TRAVEL_MODE) as TravelMode | null;
        if (savedMode && ['DRIVE', 'TRANSIT', 'WALK', 'BICYCLE'].includes(savedMode)) {
            this.travelModeSelect.value = savedMode;
        }

        const savedMax = localStorage.getItem(STORAGE_KEYS.RECOMMEND_MAX_MINUTES);
        const maxMinutes = savedMax ? Number(savedMax) : 20;
        const validMax = Number.isFinite(maxMinutes) && maxMinutes >= 5 && maxMinutes <= 120 ? maxMinutes : 20;
        this.maxMinutesInput.value = String(validMax);
        this.maxMinutesLabel.textContent = String(validMax);

        // Restore manually set address, or clear for GPS-based location
        const isManualLocation = localStorage.getItem(STORAGE_KEYS.RECOMMEND_MANUAL_LOCATION) === 'true';
        if (isManualLocation) {
            const savedAddress = localStorage.getItem(STORAGE_KEYS.RECOMMEND_ADDRESS);
            const savedLat = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LAT);
            const savedLng = localStorage.getItem(STORAGE_KEYS.RECOMMEND_LNG);
            this.addressInput.value = savedAddress || '';
            this.configLat = savedLat ? Number(savedLat) : null;
            this.configLng = savedLng ? Number(savedLng) : null;
        } else {
            this.addressInput.value = '';
            this.configLat = null;
            this.configLng = null;
        }
        this.updateLocationStatus();

        // Reset restaurant list
        this.restaurantList.innerHTML = `<p class="config-empty-message">${i18n.configEmptyMessage}</p>`;

        this.overlay.classList.add('active');
        this.panel.classList.add('active');
    }

    close(): void {
        this.saveSettings();
        this.overlay.classList.remove('active');
        this.panel.classList.remove('active');
        this.onClose();
    }

    getFilteredRestaurants(): FilteredRestaurant[] {
        return this.filteredRestaurants;
    }

    getSettings(): { lat: number | null; lng: number | null; mode: TravelMode; maxMinutes: number } {
        return {
            lat: this.configLat,
            lng: this.configLng,
            mode: this.travelModeSelect.value as TravelMode,
            maxMinutes: Number(this.maxMinutesInput.value)
        };
    }

    setCoordinates(lat: number, lng: number): void {
        this.configLat = lat;
        this.configLng = lng;
    }

    private saveSettings(): void {
        const travelMode = this.travelModeSelect.value as TravelMode;
        const maxMinutes = Number(this.maxMinutesInput.value);

        localStorage.setItem(STORAGE_KEYS.RECOMMEND_TRAVEL_MODE, travelMode);
        localStorage.setItem(STORAGE_KEYS.RECOMMEND_MAX_MINUTES, String(maxMinutes));

        if (this.configLat !== null && this.configLng !== null) {
            localStorage.setItem(STORAGE_KEYS.RECOMMEND_LAT, String(this.configLat));
            localStorage.setItem(STORAGE_KEYS.RECOMMEND_LNG, String(this.configLng));
            const address = this.addressInput.value.trim();
            if (address) {
                localStorage.setItem(STORAGE_KEYS.RECOMMEND_ADDRESS, address);
            }
        }
    }

    private updateLocationStatus(): void {
        if (this.addressInput.value.trim().length > 0) {
            if (this.configLat !== null && this.configLng !== null) {
                this.locationStatus.textContent = i18n.addressRecognizedSuccess;
                this.locationStatus.className = 'config-location-status success';
            } else {
                this.locationStatus.textContent = i18n.willUseTypedAddress;
                this.locationStatus.className = 'config-location-status';
            }
        } else if (this.configLat !== null && this.configLng !== null) {
            this.locationStatus.textContent = i18n.willUseCurrentLocation;
            this.locationStatus.className = 'config-location-status';
        } else {
            this.locationStatus.textContent = i18n.pleaseSelectLocation;
            this.locationStatus.className = 'config-location-status';
        }
    }

    private async populateFromCurrentLocation(): Promise<void> {
        if (!navigator.geolocation) {
            this.locationStatus.textContent = i18n.geoNotSupported;
            return;
        }

        this.locationStatus.textContent = i18n.gettingLocation;

        try {
            const position = await this.location.getCurrentPosition();

            this.configLat = position.coords.latitude;
            this.configLng = position.coords.longitude;

            // Clear the address input since we're using GPS location
            this.addressInput.value = '';

            // Save to localStorage for quick recommend
            localStorage.setItem(STORAGE_KEYS.RECOMMEND_LAT, String(this.configLat));
            localStorage.setItem(STORAGE_KEYS.RECOMMEND_LNG, String(this.configLng));
            // Clear manual location flag so GPS auto-refresh can resume
            localStorage.removeItem(STORAGE_KEYS.RECOMMEND_MANUAL_LOCATION);

            const geoResult = await this.location.reverseGeocode(this.configLat, this.configLng, this.api);
            localStorage.setItem(STORAGE_KEYS.RECOMMEND_ADDRESS, geoResult.address);
            const normalizedCountry = normalizeCountryCode(geoResult.country_code);
            if (normalizedCountry) {
                localStorage.setItem(STORAGE_KEYS.RECOMMEND_COUNTRY, normalizedCountry);
            }
            this.locationStatus.textContent = `${i18n.currentLocationPrefix} ${geoResult.address}`;
            this.locationStatus.className = 'config-location-status';
        } catch (error) {
            const errorMsg = error instanceof GeolocationPositionError
                ? this.location.getErrorMessage(error)
                : i18n.locatingFailed;
            this.locationStatus.textContent = errorMsg;
        }
    }

    async apply(): Promise<void> {
        const maxMinutes = Number(this.maxMinutesInput.value);
        const travelMode = this.travelModeSelect.value as TravelMode;
        const address = this.addressInput.value.trim();

        // Validate we have a location
        if (this.configLat === null || this.configLng === null) {
            if (!address) {
                try {
                    await this.populateFromCurrentLocation();
                    if (this.configLat === null || this.configLng === null) {
                        return;
                    }
                } catch {
                    return;
                }
            }
        }

        // Save preferences
        localStorage.setItem(STORAGE_KEYS.RECOMMEND_TRAVEL_MODE, travelMode);
        localStorage.setItem(STORAGE_KEYS.RECOMMEND_MAX_MINUTES, String(maxMinutes));

        // Show loading
        this.restaurantList.innerHTML = `<p class="config-empty-message">${i18n.configLoadingMessage}</p>`;
        this.applyBtn.disabled = true;

        try {
            const payload: Record<string, unknown> = {
                session_id: this.sessionId,
                max_travel_time_minutes: maxMinutes,
                travel_mode: travelMode
            };

            if (this.configLat !== null && this.configLng !== null) {
                payload.origin_lat = this.configLat;
                payload.origin_lng = this.configLng;
            } else if (address) {
                payload.origin_address = address;
            }

            const data = await this.api.fetch<{ restaurants: FilteredRestaurant[] }>('api/restaurant-config', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            this.filteredRestaurants = data.restaurants;
            this.renderFilteredRestaurants();
            this.onFilterApplied(data.restaurants);
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            this.restaurantList.innerHTML = `<p class="config-empty-message">${i18n.loadFailed}: ${errorMsg}</p>`;
        } finally {
            this.applyBtn.disabled = false;
        }
    }

    private renderFilteredRestaurants(): void {
        if (this.filteredRestaurants.length === 0) {
            this.restaurantList.innerHTML = `<p class="config-empty-message">${i18n.configNoResults}</p>`;
            return;
        }

        // Sort restaurants by travel time (ascending)
        const sortedRestaurants = [...this.filteredRestaurants].sort((a, b) => {
            const aDuration = a.travel?.duration_seconds ?? Infinity;
            const bDuration = b.travel?.duration_seconds ?? Infinity;
            return aDuration - bDuration;
        });

        this.restaurantList.innerHTML = '';

        // Add count header
        const countHeader = document.createElement('div');
        countHeader.className = 'config-restaurant-count';
        countHeader.textContent = `${sortedRestaurants.length} ${i18n.configRestaurantCount}`;
        this.restaurantList.appendChild(countHeader);

        // Add restaurants
        const isZh = getLanguage() === 'zh';
        for (const r of sortedRestaurants) {
            const item = document.createElement('div');
            item.className = 'config-restaurant-item';

            const name = isZh ? r.name_zh : r.name_en;
            const altName = isZh ? r.name_en : r.name_zh;
            const showAltName = altName && altName !== name;

            let html = `<div class="config-restaurant-name">${name}`;
            if (showAltName) {
                html += ` <span style="font-weight: normal; color: var(--text-light); font-size: 0.85em;">(${altName})</span>`;
            }
            html += `</div>`;
            html += `<div class="config-restaurant-cuisine">${r.cuisine}</div>`;

            if (r.travel && r.travel.duration_text) {
                html += `<div class="config-restaurant-travel">${r.travel.duration_text}</div>`;
            }

            item.innerHTML = html;
            this.restaurantList.appendChild(item);
        }
    }

    private scheduleAutocomplete(): void {
        if (this.autocompleteTimer !== null) {
            window.clearTimeout(this.autocompleteTimer);
        }
        this.autocompleteTimer = window.setTimeout(() => {
            void this.handleAutocomplete();
        }, 300);
    }

    private async handleAutocomplete(): Promise<void> {
        const text = this.addressInput.value.trim();

        if (text.length < 2) {
            this.addressAutocomplete.classList.add('hidden');
            return;
        }

        const requestId = ++this.autocompleteRequestId;

        try {
            const response = await fetch('api/address-autocomplete', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    input: text,
                    country_code: getPreferredCountryCode(),
                    language_code: getPlacesLanguageCode(),
                    location_lat: getStoredLocation()?.lat ?? null,
                    location_lng: getStoredLocation()?.lng ?? null
                })
            });

            if (requestId !== this.autocompleteRequestId) return;

            if (!response.ok) {
                this.addressAutocomplete.classList.add('hidden');
                return;
            }

            const data = await response.json();
            if (requestId !== this.autocompleteRequestId) return;

            if (data.predictions && data.predictions.length > 0) {
                this.addressAutocomplete.innerHTML = '';
                this.addressAutocomplete.classList.remove('hidden');

                for (const prediction of data.predictions) {
                    const item = document.createElement('div');
                    item.className = 'address-autocomplete-item';
                    item.innerHTML = `
                        <div class="address-autocomplete-item-main">${prediction.main_text}</div>
                        <div class="address-autocomplete-item-secondary">${prediction.secondary_text}</div>
                    `;
                    item.addEventListener('click', () => {
                        this.selectAddress(prediction);
                    });
                    this.addressAutocomplete.appendChild(item);
                }
            } else {
                this.addressAutocomplete.classList.add('hidden');
            }
        } catch {
            if (requestId !== this.autocompleteRequestId) return;
            this.addressAutocomplete.classList.add('hidden');
        }
    }

    private async selectAddress(prediction: { place_id: string; main_text: string; secondary_text: string }): Promise<void> {
        this.addressInput.value = `${prediction.main_text}, ${prediction.secondary_text}`;
        this.addressAutocomplete.classList.add('hidden');

        try {
            const response = await fetch('api/place-details', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    place_id: prediction.place_id,
                    language_code: getPlacesLanguageCode()
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.latitude !== null && data.longitude !== null) {
                    this.configLat = data.latitude;
                    this.configLng = data.longitude;
                    // Mark that user manually selected a location
                    localStorage.setItem(STORAGE_KEYS.RECOMMEND_MANUAL_LOCATION, 'true');
                    const normalizedCountry = normalizeCountryCode(data.country_code);
                    if (normalizedCountry) {
                        localStorage.setItem(STORAGE_KEYS.RECOMMEND_COUNTRY, normalizedCountry);
                    }
                    this.locationStatus.textContent = i18n.addressRecognizedSuccess;
                    this.locationStatus.className = 'config-location-status success';
                }
            }
        } catch (error) {
            console.error('Error getting place details:', error);
        }
    }

    updateLanguage(): void {
        // Update config panel labels
        const labels = this.panel.querySelectorAll('label');
        if (labels[0]) {
            labels[0].childNodes[0].textContent = i18n.configStartingLocation;
        }
        if (labels[1]) {
            labels[1].childNodes[0].textContent = i18n.configTravelMethod;
        }
        if (labels[2]) {
            const span = labels[2].querySelector('span');
            const currentValue = span ? span.textContent : '20';
            labels[2].childNodes[0].textContent = i18n.configMaxTravelTime + ': ';
            if (span) span.textContent = currentValue;
        }

        // Update buttons
        this.useCurrentBtn.textContent = i18n.configUseCurrentLocation;
        this.applyBtn.textContent = i18n.configPreviewBtn;

        // Update placeholder
        this.addressInput.placeholder = i18n.configOrTypeAddress;

        // Update select options
        const options = this.travelModeSelect.querySelectorAll('option');
        if (options[0]) options[0].textContent = i18n.configTravelModeDrive;
        if (options[1]) options[1].textContent = i18n.configTravelModeTransit;
        if (options[2]) options[2].textContent = i18n.configTravelModeWalk;
        if (options[3]) options[3].textContent = i18n.configTravelModeBike;

        // Update empty message if showing
        const emptyMsg = this.restaurantList.querySelector('.config-empty-message');
        if (emptyMsg && emptyMsg.textContent &&
            (emptyMsg.textContent.includes('Configure') || emptyMsg.textContent.includes('配置'))) {
            emptyMsg.textContent = i18n.configEmptyMessage;
        }
    }
}
